<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useDataStore } from '@/store/dataStore';
import orderService from '@/services/orderService';
import memberService from '@/services/memberService';
import Swal from 'sweetalert2';

export default {
  setup() {
    const router = useRouter();
    const { members, loadAllData } = useDataStore();
    
    const selectedMemberId = ref(null);
    const selectedMember = ref(null);
    const tanggalOrder = ref(new Date().toISOString().split('T')[0]);
    const items = ref([]);
    const isSubmitting = ref(false);
    
    // Form untuk tambah item
    const newItem = ref({
      product_id: null,
      source_id: null,
      quantity_box: 1,
      qty_type: 'dus'
    });

    const qtyTypeOptions = [
        { id: 'dus', text: 'Dus' },
        { id: 'inner', text: 'Inner' },
        { id: 'sachet', text: 'Sachet' },
        { id: 'botol', text: 'Botol' }
    ];
    
    const currentMemberProducts = ref([]);
    const currentMemberSources = ref([]);

    onMounted(() => {
        loadAllData();
    });

    const memberOptions = computed(() => {
        return members.value.map(m => ({
            id: m.id,
            text: `${m.nama_toko} - ${m.nama_member}`
        }));
    });

    const productOptions = computed(() => {
        return currentMemberProducts.value.map(p => ({
            id: p.id,
            text: p.nama_produk
        }));
    });

    const sourceOptions = computed(() => {
        return currentMemberSources.value.map(s => ({
            id: s.id,
            text: s.nama_sumber
        }));
    });

    const onMemberChange = async (memberId) => {
        if (!memberId) {
            selectedMember.value = null;
            return;
        }
        
        try {
            const memberData = await memberService.getMember(memberId);
            selectedMember.value = memberData;
            currentMemberProducts.value = memberData.products || [];
            currentMemberSources.value = memberData.sources || [];
            items.value = [];
        } catch (error) {
            console.error("Failed to load member details", error);
        }
    };

    const getProductName = (productId) => {
        const prod = currentMemberProducts.value.find(p => p.id == productId);
        return prod ? prod.nama_produk : '-';
    };

    const getSourceName = (sourceId) => {
        const src = currentMemberSources.value.find(s => s.id == sourceId);
        return src ? src.nama_sumber : '-';
    };

    const addItem = () => {
        if (!newItem.value.product_id || !newItem.value.source_id) {
            Swal.fire('Peringatan', 'Pilih Produk dan Sumber terlebih dahulu', 'warning');
            return;
        }
        
        items.value.push({
            product_id: newItem.value.product_id,
            source_id: newItem.value.source_id,
            quantity_box: newItem.value.quantity_box,
            qty_type: newItem.value.qty_type
        });
        
        // Reset form
        newItem.value = { product_id: null, source_id: null, quantity_box: 1, qty_type: 'dus' };
    };

    const removeItem = (index) => {
        items.value.splice(index, 1);
    };


    const submitOrder = async (status) => {
        if (!selectedMemberId.value) {
            Swal.fire('Error', 'Silakan pilih Kios terlebih dahulu', 'error');
            return;
        }

        if (items.value.length === 0) {
            Swal.fire('Error', 'Minimal harus ada 1 item pesanan', 'error');
            return;
        }

        isSubmitting.value = true;
        try {
            const orderData = {
                member_id: parseInt(selectedMemberId.value),
                tanggal_order: new Date(tanggalOrder.value).toISOString(),
                status: status,
                catatan: '',
                details: items.value.map(i => ({
                    product_id: parseInt(i.product_id),
                    source_id: parseInt(i.source_id),
                    quantity_box: parseInt(i.quantity_box),
                    qty_type: i.qty_type
                }))
            };

            const result = await orderService.createOrder(orderData);
            
            // Toast notification
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: status === 'final' ? 'PO Berhasil difinalisasi!' : 'PO Berhasil disimpan sebagai draft!',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
            
            router.push(`/purchases/order-details/${result.id}`);
        } catch (error) {
            console.error("Failed to submit order", error);
            Swal.fire('Error', 'Gagal memproses pesanan ke server', 'error');
        } finally {
            isSubmitting.value = false;
        }
    };

    return {
        selectedMemberId,
        selectedMember,
        tanggalOrder,
        items,
        newItem,
        isSubmitting,
        memberOptions,
        productOptions,
        sourceOptions,
        onMemberChange,
        getProductName,
        getSourceName,
        addItem,
        removeItem,
        submitOrder,
        qtyTypeOptions,
        currentStep: ref(1),
        router
    };
  }
};
</script>

<style scoped>
.custom-date-input {
    border: 1px solid #d9d9d9 !important;
    box-shadow: none !important;
    height: 40px !important;
    display: flex;
    align-items: center;
}
.custom-date-input:hover, .custom-date-input:focus {
    border-color: #029147 !important;
}
:deep(.ant-picker-focused) {
    border-color: #029147 !important;
    box-shadow: 0 0 0 2px rgba(2, 145, 71, 0.2) !important;
}
:deep(.ant-picker-today-btn) {
    color: #029147 !important;
}
:deep(.ant-picker-active-bar) {
    background: #029147 !important;
}
:deep(.ant-btn-primary) {
    background-color: #029147 !important;
    border-color: #029147 !important;
}
.themed-datepicker .input-group-text {
    border-right: 0;
}

/* Mobile Add Order Styles */
.mob-add-order {
  padding: 15px 5px 120px 5px;
  background: #fdfdfd;
}

.btn-back-mob {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e293b;
  border: 1px solid #f1f5f9;
  transition: all 0.2s;
}
.btn-back-mob:active {
  transform: scale(0.9);
  background: #f8fafc;
}

.mob-step-label {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-num {
  width: 24px;
  height: 24px;
  background: #029147;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
}

.mob-kios-summary {
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.04);
  border: 1px solid #f1f5f9;
}

.mob-item-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 10px rgba(0,0,0,0.02);
}

.item-qty-pill {
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 12px;
}

.mob-action-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 15px 20px 35px 20px;
  border-top: 1px solid #f1f5f9;
  z-index: 1100;
  box-shadow: 0 -10px 30px rgba(0,0,0,0.05);
}

.btn-premium-draft {
  background: white;
  border: 2px solid #029147;
  color: #029147;
  font-weight: 800;
  border-radius: 14px;
  height: 52px;
}

.btn-premium-final {
  background: #029147;
  border: none;
  color: white;
  font-weight: 800;
  border-radius: 14px;
  height: 52px;
  box-shadow: 0 8px 20px rgba(2, 145, 71, 0.25);
}

.btn-mob-add {
  height: 48px;
  border-radius: 12px;
  background: #029147;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.uppercase { text-transform: uppercase; letter-spacing: 0.5px; }
.smallest { font-size: 11px; }

/* Desktop Minimalist Tweak */
.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;  
  overflow: hidden;
}

.compact-select :deep(.vs__dropdown-toggle) {
  height: 38px !important;
  border-radius: 8px !important;
}

.compact-select-table :deep(.vs__dropdown-toggle) {
  height: 30px !important;
  border-radius: 6px !important;
  font-size: 12px;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #eee !important;
}

.shadow-xs {
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.btn-icon:hover {
  background: rgba(220, 53, 69, 0.1);
  border-radius: 50%;
}
</style>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper">
    <!-- MOBILE VIEW (Wizard Style) -->
    <div class="content d-lg-none mob-add-order">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4 px-2">
           <a href="javascript:void(0);" @click="currentStep === 2 ? currentStep = 1 : router.push('/purchases/order-list')" class="btn-back-mob shadow-sm">
              <vue-feather type="arrow-left" size="20"></vue-feather>
           </a>
           <h5 class="fw-bold mb-0">{{ currentStep === 1 ? 'Langkah 1: Kios' : 'Langkah 2: Item' }}</h5>
           <div style="width: 24px;"></div>
        </div>

        <!-- STEP 1: PILIH KIOS -->
        <section v-if="currentStep === 1" class="animate__animated animate__fadeIn">
           <div class="mob-step-label mb-3 px-2">
              <div class="step-num">1</div>
              <h6 class="fw-bold mb-0 text-dark">Informasi Kios & Tanggal</h6>
           </div>
           
           <div class="card border-0 shadow-none bg-transparent px-1">
              <div class="mb-3">
                 <label class="smallest fw-bold text-muted uppercase mb-1">Tanggal Pesanan</label>
                 <a-date-picker 
                    v-model:value="tanggalOrder" 
                    placeholder="Pilih Tanggal" 
                    class="form-control rounded-4 py-2 border-light-dark"
                    value-format="YYYY-MM-DD"
                  />
              </div>
              <div class="mb-4">
                 <label class="smallest fw-bold text-muted uppercase mb-1">Cari Nama Kios</label>
                 <vue-select
                    :options="memberOptions"
                    v-model="selectedMemberId"
                    @update:modelValue="onMemberChange"
                    placeholder="Ketik nama toko..."
                    class="mob-select-rounded"
                 />
              </div>
              
              <!-- Kios Selection Result -->
              <div v-if="selectedMember" class="mob-kios-summary p-3 mb-4 animate__animated animate__zoomIn">
                 <div class="d-flex align-items-center gap-3">
                    <div class="bg-success-soft p-2 rounded-3">
                       <vue-feather type="home" class="text-success" size="20"></vue-feather>
                    </div>
                    <div class="flex-fill overflow-hidden">
                       <h6 class="fw-bold mb-0 text-dark">{{ selectedMember.nama_toko }}</h6>
                       <span class="smallest text-muted fw-bold uppercase">{{ selectedMember.nama_member }}</span>
                    </div>
                 </div>
              </div>

              <div v-if="selectedMemberId" class="mt-4">
                 <button class="btn btn-premium-final w-100 d-flex align-items-center justify-content-center gap-2" @click="currentStep = 2">
                    Lanjut Pilih Barang <vue-feather type="arrow-right" size="18"></vue-feather>
                 </button>
              </div>
           </div>
        </section>

        <!-- STEP 2: TAMBAH ITEM -->
        <section v-if="currentStep === 2" class="animate__animated animate__fadeIn">
           <div class="mob-step-label mb-3 px-2">
              <div class="step-num">2</div>
              <h6 class="fw-bold mb-0 text-dark">Daftar Item Barang</h6>
           </div>

           <!-- Product Entry Form (Compact) -->
           <div class="p-3 bg-white rounded-4 border mb-4">
              <div class="row g-2">
                 <div class="col-12 mb-2">
                    <label class="smallest fw-bold text-muted uppercase mb-1">Produk</label>
                    <vue-select :options="productOptions" v-model="newItem.product_id" placeholder="Pilih Produk" />
                 </div>
                  <div class="col-7">
                     <label class="smallest fw-bold text-muted uppercase mb-1">Sumber</label>
                     <vue-select :options="sourceOptions" v-model="newItem.source_id" placeholder="Sumber" />
                  </div>
                  <div class="col-5">
                    <label class="smallest fw-bold text-muted uppercase mb-1">Tipe</label>
                    <vue-select :options="qtyTypeOptions" v-model="newItem.qty_type" placeholder="Tipe" />
                  </div>
                  <div class="col-12 mt-2">
                     <label class="smallest fw-bold text-muted uppercase mb-1">Jumlah</label>
                     <input type="number" class="form-control rounded-3" v-model="newItem.quantity_box" min="1" />
                  </div>
                 <div class="col-12 mt-3">
                    <button class="btn btn-mob-add w-100 fw-bold" @click="addItem">
                       <vue-feather type="plus" size="18" class="me-2"></vue-feather> Tambahkan Baris
                    </button>
                 </div>
              </div>
           </div>

           <!-- Item List Mobile -->
           <div v-if="items.length > 0" class="px-1">
              <div v-for="(item, index) in items" :key="'mob-item-'+index" class="mob-item-card p-3 mb-3 d-flex align-items-center gap-3 shadow-xs">
                 <div class="bg-blue-soft p-2 rounded-3">
                    <vue-feather type="package" class="text-blue" size="20"></vue-feather>
                 </div>
                 <div class="flex-fill">
                    <h6 class="fw-bold mb-0 text-dark small">{{ getProductName(item.product_id) }}</h6>
                    <span class="smallest text-muted">{{ getSourceName(item.source_id) }}</span>
                 </div>
                  <div class="text-end d-flex align-items-center gap-2">
                     <div class="item-qty-pill smallest fw-bold">{{ item.quantity_box }} <span class="uppercase">{{ item.qty_type }}</span></div>
                     <button class="btn p-0 text-danger" @click="removeItem(index)">
                       <vue-feather type="trash-2" size="18"></vue-feather>
                    </button>
                 </div>
              </div>
           </div>
           
           <div v-else class="text-center py-5 text-muted border border-dashed rounded-4 mx-2">
              <vue-feather type="shopping-cart" size="32" class="mb-2 opacity-25"></vue-feather>
              <p class="small mb-0">Keranjang masih kosong.<br>Silakan tambahkan produk di atas.</p>
           </div>
        </section>
    </div>

    <!-- ACTION FOOTER MOBILE (Step 2 Only) -->
    <div v-if="currentStep === 2" class="mob-action-footer d-lg-none animate__animated animate__slideInUp">
       <div class="row g-3">
          <div class="col-6">
             <button class="btn btn-premium-draft w-100" @click="submitOrder('draft')" :disabled="isSubmitting || items.length === 0">
                DRAFT
             </button>
          </div>
          <div class="col-6">
             <button class="btn btn-premium-final w-100" @click="submitOrder('final')" :disabled="isSubmitting || items.length === 0">
                BUAT PO
             </button>
          </div>
       </div>
    </div>

    <!-- DESKTOP VIEW (Untouched) -->
    <div class="content d-none d-lg-block">
      <!-- Page Header -->
      <div class="page-header">
        <div class="add-item d-flex pb-2">
          <div class="page-title">
            <h5 class="fw-bold mb-0">Buat PO Baru</h5>
            <span class="smallest text-muted">Pilih kios dan tambahkan produk pesanan</span>
          </div>
        </div>
        <div class="page-btn">
          <router-link to="/purchases/order-list" class="btn btn-outline-secondary">
            <vue-feather type="arrow-left" class="me-2"></vue-feather>Kembali
          </router-link>
        </div>
      </div>

      <div class="row g-3">
        <!-- Left Column: Pilih Kios -->
        <div class="col-lg-3 col-md-4">
          <div class="card">
            <div class="card-header bg-success text-white py-2">
              <h6 class="card-title mb-0 d-flex align-items-center text-white">
                <span class="badge bg-white text-success me-2 rounded-circle" style="width: 22px; height: 22px; line-height: 16px; font-size: 10px;">1</span>
                Pilih Kios
              </h6>
            </div>
            <div class="card-body p-3">
              <div class="mb-2">
                <label class="form-label smallest fw-bold mb-1">Tanggal PO</label>
                <div class="input-group themed-datepicker shadow-xs">
                  <span class="input-group-text bg-white py-1">
                    <vue-feather type="calendar" style="width: 14px; height: 14px;"></vue-feather>
                  </span>
                  <a-date-picker 
                    v-model:value="tanggalOrder" 
                    placeholder="Pilih Tanggal" 
                    class="form-control border-start-0 custom-date-input"
                    style="height: 36px !important;"
                    value-format="YYYY-MM-DD"
                  />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label smallest fw-bold mb-1">Pilih Kios</label>
                <vue-select
                  :options="memberOptions"
                  v-model="selectedMemberId"
                  @update:modelValue="onMemberChange"
                  placeholder="Cari Kios..."
                  class="compact-select"
                />
              </div>

              <!-- Selected Kios Card -->
              <div v-if="selectedMember" class="border rounded p-2 bg-success bg-opacity-10 border-success shadow-xs">
                <h6 class="mb-1 text-success fw-bold small text-truncate" :title="selectedMember.nama_toko">{{ selectedMember.nama_toko }}</h6>
                <div class="mb-1 text-danger smallest fw-bold text-truncate">{{ selectedMember.nama_member }}</div>
                <div class="text-muted smallest mb-1 text-truncate-2">{{ selectedMember.alamat || '-' }}</div>
                <div class="text-success smallest fw-bold">{{ selectedMember.nomor_handphone || '-' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Tambah Produk -->
        <div class="col-lg-9 col-md-8">
          <div class="card">
            <div class="card-header bg-white border-bottom py-2">
              <h6 class="card-title mb-0 d-flex align-items-center">
                <span class="badge bg-success text-white me-2 rounded-circle" style="width: 22px; height: 22px; line-height: 16px; font-size: 10px;">2</span>
                Tambah Produk
              </h6>
            </div>
            <div class="card-body">
              <!-- Form Tambah Item -->
              <div class="row g-2 mb-3" v-if="selectedMemberId">
                <div class="col-lg-3 col-md-3">
                  <label class="form-label smallest fw-bold mb-1">Produk</label>
                  <vue-select
                    :options="productOptions"
                    v-model="newItem.product_id"
                    placeholder="Pilih Produk"
                  />
                </div>
                <div class="col-lg-3 col-md-3">
                  <label class="form-label smallest fw-bold mb-1">Sumber</label>
                  <vue-select
                    :options="sourceOptions"
                    v-model="newItem.source_id"
                    placeholder="Pilih Sumber"
                  />
                </div>
                <div class="col-lg-2 col-md-2">
                  <label class="form-label smallest fw-bold mb-1">Tipe</label>
                  <vue-select
                    :options="qtyTypeOptions"
                    v-model="newItem.qty_type"
                    placeholder="Tipe"
                  />
                </div>
                <div class="col-lg-2 col-md-2">
                  <label class="form-label smallest fw-bold mb-1">Qty</label>
                  <input type="number" class="form-control" style="height: 40px;" v-model="newItem.quantity_box" min="1" />
                </div>
                <div class="col-lg-2 col-md-2 d-flex align-items-end">
                  <button class="btn btn-success w-100 fw-bold" style="height: 40px;" @click="addItem">
                    <vue-feather type="plus" style="width: 16px; height: 16px;"></vue-feather>
                    TAMBAH
                  </button>
                </div>
              </div>

              <!-- Placeholder jika belum pilih kios -->
              <div v-if="!selectedMemberId" class="text-center py-5 text-muted">
                <vue-feather type="info" class="mb-3" style="width: 40px; height: 40px;"></vue-feather>
                <p>Silakan pilih Kios terlebih dahulu di sebelah kiri.</p>
              </div>

              <!-- Daftar Item PO -->
              <div v-if="items.length > 0">
                <div class="d-flex align-items-center justify-content-between mb-1">
                    <span class="smallest fw-bold text-muted uppercase">Daftar Pesanan ({{ items.length }})</span>
                </div>
                <div class="table-responsive">
                  <table class="table table-borderless mb-0">
                    <thead>
                      <tr class="text-muted small">
                        <th>Produk</th>
                        <th>Sumber</th>
                        <th class="text-center">Tipe</th>
                        <th class="text-center">Qty</th>
                        <th class="text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in items" :key="index" class="align-middle border-bottom-dashed">
                        <td class="py-2">
                          <span class="fw-bold text-dark">{{ getProductName(item.product_id) }}</span>
                        </td>
                        <td class="py-2">
                          <span class="badge bg-light text-dark fw-normal border">
                            {{ getSourceName(item.source_id) }}
                          </span>
                        </td>
                        <td class="text-center py-2">
                          <vue-select
                            :options="qtyTypeOptions"
                            v-model="item.qty_type"
                            class="compact-select-table"
                            style="width: 100px;"
                          />
                        </td>
                        <td class="text-center py-2">
                          <input 
                            type="number" 
                            class="form-control form-control-sm text-center mx-auto" 
                            v-model="item.quantity_box" 
                            style="width: 60px; height: 30px;" 
                            min="1"
                          />
                        </td>
                        <td class="text-center py-2">
                          <button class="btn btn-icon btn-sm" @click="removeItem(index)">
                            <vue-feather type="trash-2" class="text-danger" style="width: 16px; height: 16px;"></vue-feather>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Empty State -->
              <div v-if="selectedMemberId && items.length === 0" class="text-center py-4 text-muted">
                <p class="mb-0 small">Belum ada item. Tambahkan produk di atas.</p>
              </div>

              <!-- Footer Actions (Compact) -->
              <div class="mt-4 pt-3 border-top text-end" v-if="selectedMemberId && items.length > 0">
                <button 
                  class="btn btn-outline-success btn-sm me-2 px-3 fw-bold" 
                  @click="submitOrder('draft')" 
                  :disabled="isSubmitting"
                >
                  <vue-feather type="save" class="me-1" style="width: 14px; height: 14px;"></vue-feather>
                  Simpan Draft
                </button>
                <button 
                  class="btn btn-success btn-sm px-3 fw-bold" 
                  @click="submitOrder('final')" 
                  :disabled="isSubmitting"
                >
                  <vue-feather type="check-circle" class="me-1" style="width: 14px; height: 14px;"></vue-feather>
                  Finalisasi PO
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card-header.bg-success {
  border-radius: 0.5rem 0.5rem 0 0 !important;
}
.badge.rounded-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
